import { useState } from "react";
import { Check, ChevronDown, ChevronUp, Phone } from "lucide-react";
import PricingCard from "./PricingCard";
import ContactSalesModal from "../../ai/misc/ContactSalesModal";
import AgreementModal from "../../misc/AgreementModal";

import { subscribeToPlan } from "../../../../services/api/billing";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import AddOnsConsole from "./AddOnsConsole";
import SelectedFeaturesFloating, {
  SelectedItem,
} from "../../misc/SelectedFeaturesFloating";
import { Zap } from "lucide-react";
import { offeringsService } from "../../../../services/api/settings";
import { OfferingPlan, OfferingAddon } from "../../../../store/types/settings";
import { useEffect } from "react";

export default function PricingPage() {
  const [isYearly, setIsYearly] = useState(false);
  const [showComparison, setShowComparison] = useState(false);
  const [showAddOns, setShowAddOns] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  const [agreementModalState, setAgreementModalState] = useState<{
    isOpen: boolean;
    planTitle?: string;
  }>({
    isOpen: false,
  });
  const [offeringPlans, setOfferingPlans] = useState<OfferingPlan[]>([]);
  const [offeringAddons, setOfferingAddons] = useState<OfferingAddon[]>([]);
  const [loading, setLoading] = useState(true);

  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const organizationId = searchParams.get("Id");
  const urlTier = searchParams.get("tier");
  const [selectedTier, setSelectedTier] = useState<string | null>(
    () => urlTier,
  );
  const id = organizationId;

  const navigate = useNavigate();
  const [selectedDeploymentId, setSelectedDeploymentId] = useState<
    string | null
  >(null);
  const [selectedIntegrationIds, setSelectedIntegrationIds] = useState<
    string[]
  >(() => {
    const ids = searchParams.get("integrationIds");
    return ids ? ids.split(",").filter((id) => id) : [];
  });

  const handleDeploymentChange = (id: string) => {
    setSelectedDeploymentId(id);
  };

  const toggleIntegration = (id: string) => {
    setSelectedIntegrationIds((prev) => {
      const newIds = prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id];

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("integrationIds", newIds.join(","));
        } else {
          p.delete("integrationIds");
        }
        return p;
      });
      return newIds;
    });
  };

  useEffect(() => {
    const fetchOfferings = async () => {
      try {
        setLoading(true);
        const response = await offeringsService.getAll();
        if (response.status === "success" && response.data) {
          setOfferingPlans([...response.data.CONSOLE.plans].reverse());
          setOfferingAddons([...response.data.CONSOLE.addons].reverse());
        }
      } catch (error) {
        console.error("Error fetching console offerings:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOfferings();
  }, []);

  const deploymentMainOptions = offeringAddons
    .filter((a) => a.category === "DEPLOYMENT_COST")
    .map((a) => ({
      id: a.id,
      name: a.name,
      price: typeof a.price === "string" ? parseFloat(a.price) : a.price,
    }));

  const deploymentFixedAddOns = offeringAddons
    .filter((a) => a.category === "DEPLOYMENT_ADDON") // Assuming DEPLOYMENT_ADDON category for these
    .map((a) => ({
      id: a.id,
      name: a.name,
      price: typeof a.price === "string" ? parseFloat(a.price) : a.price,
    }))
    .slice(0, 2); // Taking first 2 as fixed if they exist

  const plans = offeringPlans.map((plan) => ({
    id: plan.id,
    title: plan.title,
    monthlyPrice: parseFloat(plan.monthlyPrice),
    yearlyPrice: parseFloat(plan.yearlyPrice),
    users: plan.subtitle,
    description: plan.description,
    features: plan.points,
    isHighlighted: plan.tier === "ENTERPRISE",
    isActive: plan.isActive,
    tier: plan.tier,
    integrationServices:
      (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
        ? plan.costBreakdown!.deploymentCosts.map((d: any) => ({
            name: d.name,
            cost: d.price.toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            }),
          }))
        : plan.setupFee || plan.integrationCost
          ? [
              {
                name: "Standard Onboarding & Training",
                cost: (
                  parseFloat(plan.setupFee || plan.integrationCost || "0") || 0
                ).toLocaleString(),
              },
            ]
          : [],
  }));

  // Helper to get all selected deployment cost IDs (Main option + Fixed add-ons if any option selected)
  const getFullDeploymentIds = () => {
    if (!selectedDeploymentId) return [];
    return [selectedDeploymentId, ...deploymentFixedAddOns.map((d) => d.id)];
  };

  const integrations = offeringAddons
    .filter((a) => a.category === "INTEGRATION")
    .map((a) => ({
      id: a.id,
      name: a.name,
      price: typeof a.price === "string" ? parseFloat(a.price) : a.price,
      currency: a.currency,
      billingType: a.billingType,
      isActive: a.isActive,
    }));

  const handleGetStarted = (plan: any) => {
    if (!organizationId) {
      toast.error(
        "Organization ID not found. Please start from the dashboard.",
      );
      return;
    }

    if (!selectedDeploymentId) {
      toast.error("Please choose deployment cost");
      return;
    }

    // Open agreement modal
    setAgreementModalState({
      isOpen: true,
      planTitle: plan.title,
    });
  };

  const handleAgreementConfirm = () => {
    if (!agreementModalState.planTitle) return;

    // Find the plan by title
    const plan = plans.find((p) => p.title === agreementModalState.planTitle);
    if (!plan) return;

    // Construct data for routing
    const deploymentIds = getFullDeploymentIds();
    const query = new URLSearchParams({
      Id: organizationId!,
      tier: plan.title,
      planId: plan.id,
      frequency: isYearly ? "YEARLY" : "MONTHLY",
      deploymentIds: deploymentIds.join(","),
      integrationIds: selectedIntegrationIds.join(","),
    }).toString();

    navigate(`/pricing/console/pay?${query}`);
    setAgreementModalState({ isOpen: false });
  };

  const selectedIntegrations = integrations.filter((i) =>
    selectedIntegrationIds.includes(i.id),
  );

  const selectedDeploymentOption = deploymentMainOptions.find(
    (d) => d.id === selectedDeploymentId,
  );

  const currentPlan = plans.find((p) => p.title === selectedTier);
  const planFeatures = currentPlan
    ? currentPlan.features.map((f) => ({
        id: f,
        displayName: f,
        price: 0,
      }))
    : [];

  const allSelectedItems: SelectedItem[] = [
    ...planFeatures,
    ...(selectedDeploymentOption
      ? [
          {
            id: selectedDeploymentOption.id,
            displayName: selectedDeploymentOption.name,
            price: selectedDeploymentOption.price,
          },
          ...deploymentFixedAddOns.map((d) => ({
            id: d.id,
            displayName: d.name,
            price: d.price,
          })),
        ]
      : []),
    ...selectedIntegrations.map((i) => ({
      id: i.id,
      displayName: i.name,
      price: i.price,
    })),
  ];

  const calculateFloatingTotal = () => {
    const total = allSelectedItems.reduce(
      (sum, item) => sum + (item.price || 0),
      0,
    );
    return total.toLocaleString();
  };

  const formatPrice = (price: number | string) => {
    if (typeof price === "string") return price;
    return new Intl.NumberFormat("en-NG", {
      style: "decimal",
      // currency: "NGN",
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-abel">
      <h1 className="text-lg md:text-[30px] text-primary pt-3 leading-relaxed text-center">
        25th Console
      </h1>
      {selectedTier && (
        <div className="flex justify-center mt-4">
          <button
            onClick={() => {
              setSelectedTier(null);
              setSearchParams((prev) => {
                prev.delete("tier");
                return prev;
              });
            }}
            className="px-6 py-2 bg-white border border-blue-600 text-blue-600 rounded-full hover:bg-blue-50 transition-colors font-medium flex items-center gap-2"
          >
            <Zap className="w-4 h-4" />
            Compare all plans
          </button>
        </div>
      )}
      {/* ... toggle section ... */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-3 mb-6">
        <div className="flex justify-center">
          <div className="bg-white rounded-full p-1.5 shadow-lg inline-flex gap-1">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-8 py-3 rounded-full font-normal transition-all duration-200 ${
                !isYearly
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-700 hover:text-gray-900"
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-8 py-3 rounded-full font-normal transition-all duration-200 ${
                isYearly
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-gray-700 hover:text-gray-900"
              }`}
            >
              Annual
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="grid md:grid-cols-3 gap-3">
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="bg-gray-100 rounded-[20px] shadow-sm h-[600px] animate-pulse border border-gray-100"
                ></div>
              ))
            : plans
                .filter((p) => !selectedTier || p.title === selectedTier)
                .map((plan, index) => (
                  <PricingCard
                    key={index}
                    {...plan}
                    isYearly={isYearly}
                    // Pass deployment data
                    deploymentMainOptions={deploymentMainOptions}
                    deploymentFixedAddOns={deploymentFixedAddOns}
                    selectedDeploymentId={selectedDeploymentId}
                    onDeploymentChange={handleDeploymentChange}
                    // Pass integration data
                    allIntegrations={integrations} // Pass full list for rendering selection inside card if needed, OR just pass selected ones.
                    // The user requirement says "Let the Custom Integration card be selectable, when each card is clicked, let title and price show on the PricingCard"
                    // So the Picking happens in the main page (Integration section), but the Card shows the summary.
                    selectedIntegrationIds={selectedIntegrationIds}
                    onRemoveIntegration={(id) => toggleIntegration(id)}
                    onSubscribe={() => handleGetStarted(plan)}
                    isTierSelected={!!selectedTier}
                  />
                ))}
        </div>
      </section>

      <AddOnsConsole
        organizationId={organizationId || undefined}
        selectedIds={selectedIntegrationIds}
        onToggle={toggleIntegration}
      />

      {/* Custom Integrations */}
      {/* <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
        <div className="bg-white rounded-[20px] shadow-lg p-8">
          <h2 className="text-2xl font-medium text-gray-900 mb-2">Add-ons</h2>
          <p className="text-gray-600 mb-8">
            Connect with your existing tools and systems
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {integrations.map((integration) => {
              const isSelected = selectedIntegrationIds.includes(
                integration.id
              );
              return (
                <div
                  key={integration.id}
                  onClick={() => toggleIntegration(integration.id)}
                  className={`cursor-pointer border rounded-[12px] p-6 transition-all duration-200 ${
                    isSelected
                      ? "border-blue-500 bg-blue-50 shadow-md ring-2 ring-blue-200"
                      : "border-gray-200 hover:border-blue-300 hover:shadow-md"
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center mb-4">
                      <integration.icon className="w-6 h-6 text-blue-600" />
                    </div>
                    {isSelected && (
                      <div className="bg-blue-500 text-white p-1 rounded-full">
                        <Check className="w-4 h-4" />
                      </div>
                    )}
                  </div>
                  <h3 className="font-medium text-gray-900 mb-2">
                    {integration.name}
                  </h3>
                  <div className="flex items-center gap-2 text-blue-600">
                    <span className="font-medium text-lg">
                      {formatPrice(integration.price)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section> */}

      {/* Feature Comparison */}
      <section className="max-w-[900px] mx-auto mb-10">
        <div className="bg-white rounded-[15px] border border-primary shadow-lg px-4 py-3">
          <button
            onClick={() => setShowComparison(!showComparison)}
            className="w-full flex items-center justify-between text-left"
          >
            <h2 className="text-[15px] md:text-[17px] font-medium text-gray-900">
              Full Feature Comparison
            </h2>
            {showComparison ? (
              <ChevronUp className="w-6 h-6 text-gray-600" />
            ) : (
              <ChevronDown className="w-6 h-6 text-gray-600" />
            )}
          </button>

          {showComparison && (
            <div className="mt-8 overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-medium text-gray-900">
                      Feature
                    </th>
                    <th className="text-center py-4 px-4 font-medium text-gray-900">
                      Essential
                    </th>
                    <th className="text-center py-4 px-4 font-medium text-gray-900">
                      Business
                    </th>
                    <th className="text-center py-4 px-4 font-medium text-gray-900">
                      Enterprise
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="bg-gray-50">
                    <td
                      colSpan={4}
                      className="py-3 px-4 font-medium text-gray-900"
                    >
                      Core Features
                    </td>
                  </tr>
                  {[
                    "Dashboard Statistics",
                    "User Management",
                    "Activity Logs",
                    "Files Manager",
                    "Teams Directory",
                    "Meetings Manager",
                    "Kanban Tasks",
                  ].map((feature) => (
                    <tr key={feature} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-gray-700">{feature}</td>
                      <td className="text-center py-3 px-4">
                        <Check className="w-5 h-5 text-gray-900 mx-auto" />
                      </td>
                      <td className="text-center py-3 px-4">
                        <Check className="w-5 h-5 text-gray-900 mx-auto" />
                      </td>
                      <td className="text-center py-3 px-4">
                        <Check className="w-5 h-5 text-gray-900 mx-auto" />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td
                      colSpan={4}
                      className="py-3 px-4 font-medium text-gray-900"
                    >
                      Advanced Features
                    </td>
                  </tr>
                  {[
                    "Password Vault",
                    "Knowledge Centre",
                    "Birthday & Anniversary Tracker",
                    "Products & Services Tracker",
                    "Ticketing System",
                  ].map((feature) => (
                    <tr key={feature} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-gray-700">{feature}</td>
                      <td className="text-center py-3 px-4 text-gray-400">—</td>
                      <td className="text-center py-3 px-4">
                        <Check className="w-5 h-5 text-gray-900 mx-auto" />
                      </td>
                      <td className="text-center py-3 px-4">
                        <Check className="w-5 h-5 text-gray-900 mx-auto" />
                      </td>
                    </tr>
                  ))}
                  <tr className="bg-gray-50">
                    <td
                      colSpan={4}
                      className="py-3 px-4 font-medium text-gray-900"
                    >
                      HR & Recruitment
                    </td>
                  </tr>
                  {[
                    "Clients Master Record",
                    "Jobs Master Record",
                    "KPI Task Analytics",
                    "KPI Ticketing Analytics",
                    "HR Recruitment ATS",
                    "Candidates Master Record",
                    "Job Match System",
                  ].map((feature) => (
                    <tr key={feature} className="border-b border-gray-100">
                      <td className="py-3 px-4 text-gray-700">{feature}</td>
                      <td className="text-center py-3 px-4 text-gray-400">—</td>
                      <td className="text-center py-3 px-4 text-gray-400">—</td>
                      <td className="text-center py-3 px-4">
                        <Check className="w-5 h-5 text-gray-900 mx-auto" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-700 py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => setShowContactModal(true)}
              className="bg-blue-500 hover:bg-blue-400 text-white px-8 py-4 rounded-lg font-medium text-lg transition-all duration-200 shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
            >
              <Phone className="w-5 h-5" />
              Contact Sales
            </button>
          </div>
        </div>
      </section>
      <ContactSalesModal
        isOpen={showContactModal}
        onClose={() => setShowContactModal(false)}
      />
      {agreementModalState.isOpen && (
        <AgreementModal
          isOpen={agreementModalState.isOpen}
          onClose={() => setAgreementModalState({ isOpen: false })}
          onConfirm={handleAgreementConfirm}
          billingType={isYearly ? "Yearly" : "Monthly"}
          selectedFeatures={[]}
          selectedDirectAddOnIds={selectedIntegrationIds}
          link={`/agreement?Id=${id}&tier=${agreementModalState.planTitle || ""}`}
          title={agreementModalState.planTitle || ""}
          tier={agreementModalState.planTitle || ""}
          id={organizationId || ""}
        />
      )}
      <SelectedFeaturesFloating
        selectedItems={allSelectedItems}
        onRemove={(id) => {
          if (id === selectedDeploymentId) {
            setSelectedDeploymentId(null);
          } else {
            toggleIntegration(id);
          }
        }}
        totalPrice={calculateFloatingTotal()}
        billingType={isYearly ? "Yearly" : "Monthly"}
        title="Console selection"
      />
    </div>
  );
}
